import React, { useCallback, useState } from 'react';
import { Loader2, Save, X } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { InlineMicButton } from '../InlineMicButton';
import { OBConsultMeasurementsForm } from '../OBConsultMeasurementsForm';
import type { ClinicalNote } from '../../types/clinical';
import type { OBConsultMeasurements, Pregnancy } from '../../types/pregnancy';

interface Props {
  patientId: string;
  /** Locked at open time — never editable by the user. */
  pregnancyId: string;
  /** Active pregnancies — passed from ClinicalPage to avoid a redundant fetch. */
  activePregnancies: Pregnancy[];
  onSaved: (note: ClinicalNote) => void;
  onCancel: () => void;
}

const emptyMeasurements: OBConsultMeasurements = {
  gestationWeeks: null,
  gestationDays: null,
  sfh: null,
  presentation: '',
  headAboveBrim: null,
  fetalHeartRate: null,
  fetalMovements: null,
  bpSystolic: null,
  bpDiastolic: null,
  urine: '',
  haemoglobin: null,
  supplements: { iron: false, folate: false, calcium: false },
  tbScreenDone: false,
  nextVisitDate: null,
};

/**
 * Self-contained obstetric consultation form.
 * Renders OBConsultMeasurementsForm (reused) + SOAP text fields.
 * All form state is local — nothing pushed to the ClinicalPage reducer.
 */
export function NewObsConsultForm({ patientId, pregnancyId, activePregnancies, onSaved, onCancel }: Props) {
  const { post } = useApi();

  // OB measurements
  const [measurements, setMeasurements] = useState<OBConsultMeasurements>(emptyMeasurements);

  // SOAP fields
  const [subjective, setSubjective] = useState('');
  const [objective, setObjective] = useState('');
  const [assessment, setAssessment] = useState('');
  const [plan, setPlan] = useState('');
  const [summary, setSummary] = useState('');
  const [followUp, setFollowUp] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError('');
    try {
      const res = await post<ClinicalNote>(`/clinical/${patientId}/notes`, {
        noteType: 'obstetric',
        pregnancyId,
        obstetrics: measurements,
        subjective,
        objective,
        assessment,
        plan,
        summary,
        followUp,
        icd10Suggested: [],
        icd10Confirmed: [],
        nhrplCodes: [],
        consumables: [],
        vitalSigns: {
          bp: measurements.bpSystolic && measurements.bpDiastolic
            ? `${measurements.bpSystolic}/${measurements.bpDiastolic}` : '',
          weight: '',
        },
        rawTranscript: '',
        audioGcsPath: '',
        appointmentId: '',
      });
      if (res.success && res.data) {
        onSaved(res.data);
      } else {
        setError(res.error || 'Failed to save obstetric consultation');
      }
    } catch {
      setError('Failed to save obstetric consultation');
    } finally {
      setSaving(false);
    }
  }, [patientId, pregnancyId, measurements, subjective, objective, assessment, plan, summary, followUp, post, onSaved]);

  return (
    <div className="mb-6 bg-teal-50/50 border border-teal-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-teal-800 uppercase tracking-wider">New Obstetric Consultation</h3>
        <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
      </div>

      {error && <div className="mb-3 p-2 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {/* OB Measurements — reuses existing component; pregnancy selector is read-only (pregnancyId locked from props) */}
      <OBConsultMeasurementsForm
        measurements={measurements}
        onChange={setMeasurements}
        pregnancies={activePregnancies}
        selectedPregnancyId={pregnancyId}
        onSelectPregnancy={() => {}}
        onNewPregnancy={() => {}}
      />

      {/* SOAP section */}
      <div className="space-y-3 mt-4">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Clinical Notes</h4>
        {(['subjective', 'objective', 'assessment', 'plan'] as const).map(field => {
          const value = { subjective, objective, assessment, plan }[field];
          const setter = { subjective: setSubjective, objective: setObjective, assessment: setAssessment, plan: setPlan }[field];
          return (
            <div key={field}>
              <div className="flex items-center gap-1 mb-1">
                <label className="text-xs font-medium text-gray-500 capitalize">{field}</label>
                <InlineMicButton onTranscript={t => setter(prev => prev ? prev + ' ' + t : t)} />
              </div>
              <textarea
                value={value}
                onChange={e => setter(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                placeholder={field === 'subjective' ? 'Patient complaints, symptoms...' : field === 'objective' ? 'Examination findings...' : field === 'assessment' ? 'Clinical impression...' : 'Management plan...'}
              />
            </div>
          );
        })}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="flex items-center gap-1 mb-1">
              <label className="text-xs font-medium text-gray-500">Summary</label>
              <InlineMicButton onTranscript={t => setSummary(prev => prev ? prev + ' ' + t : t)} />
            </div>
            <textarea value={summary} onChange={e => setSummary(e.target.value)} rows={2} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-teal-500 focus:border-teal-500" placeholder="Brief summary..." />
          </div>
          <div>
            <div className="flex items-center gap-1 mb-1">
              <label className="text-xs font-medium text-gray-500">Follow-up</label>
              <InlineMicButton onTranscript={t => setFollowUp(prev => prev ? prev + ' ' + t : t)} />
            </div>
            <textarea value={followUp} onChange={e => setFollowUp(e.target.value)} rows={2} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-teal-500 focus:border-teal-500" placeholder="Follow-up plan..." />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 mt-4 pt-4 border-t border-teal-200">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? 'Saving...' : 'Save Obstetric Consultation'}
        </button>
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
      </div>
    </div>
  );
}
